import { getTeamsSearchOptions } from "@aura-frontend/omnibus-az-client";
import { mergeRefs } from "@aura-frontend/react";
import { useQuery } from "@tanstack/react-query";
import { Trash, X } from "lucide-react";
import { forwardRef, useMemo, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { usaStates } from "typed-usa-states";
import { z } from "zod";

import { accountFormSchema } from "../AccountForm";
import { AutoComplete } from "../autocomplete";
import { Button } from "../ui/Button";

interface SearchStateComboboxProps {
  valueSearchPlaceholder: string;
  teamType: z.infer<typeof accountFormSchema>["teamType"];
  selectedTeam: z.infer<typeof accountFormSchema>["team"];
  onChange: (
    value: z.infer<typeof accountFormSchema>["team"] | undefined,
  ) => void;
}

const SearchTeamCombobox = forwardRef<HTMLDivElement, SearchStateComboboxProps>(
  ({ valueSearchPlaceholder, teamType, selectedTeam, onChange }, ref) => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
    const [state, setState] = useState("");

    const containerRef = useRef<HTMLDivElement>(null);

    useDebounce(
      () => {
        setDebouncedSearchValue(searchValue);
      },
      500,
      [searchValue],
    );

    // The visibility group is dynamic based on the team external id so we support both College Splits ID and Perfect Game
    const visibilityGroup = teamType.startsWith("CSB")
      ? "COLLEGE_SPLITS_BASEBALL"
      : "PERFECT_GAME";

    const { data: teams, isLoading } = useQuery({
      ...getTeamsSearchOptions({
        query: {
          name: debouncedSearchValue,
          visibilityGroup,
          region: state,
        },
      }),
    });

    const items = useMemo(
      () =>
        teams?.teams
          .filter((team) => !selectedTeam || team.id !== selectedTeam.id)
          .map((team) => {
            const value = {
              id: team.id ?? "",
              name: team.known_names?.[0] ?? "",
              externalId: team.external_ids?.[0] ?? "",
            };

            return {
              value: JSON.stringify(value),
              label: value.name,
            };
          }),
      [teams?.teams, selectedTeam],
    );

    return (
      <div>
        {!selectedTeam && (
          <div className="w-full flex" ref={mergeRefs(ref, containerRef)}>
            <AutoComplete
              items={items}
              placeholder={valueSearchPlaceholder}
              onChange={setSearchValue}
              isLoading={isLoading}
              onSelect={(rawTeam) => {
                onChange(JSON.parse(rawTeam));
              }}
            />

            <div className="relative w-1/3">
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                aria-label="State Filter"
                className={`flex h-10 w-full appearance-none rounded-sm rounded-l-none border border-gray-300 bg-input px-3 py-2 pr-8 text-sm text-text-input placeholder:font-light placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${!state ? "text-placeholder font-light" : ""}`}
              >
                <option value="" disabled hidden>
                  State Filter
                </option>
                {usaStates.map((s) => (
                  <option key={s.name} value={s.name} className="text-black">
                    {s.name}
                  </option>
                ))}
              </select>

              {/* Chevron icon */}
              {!state && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4 opacity-50"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}

              {/* Clear button */}
              {state && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-3 mt-[1px] top-1/2 -translate-y-1/2 transform h-6 w-6 hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    setState("");
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}

        {selectedTeam && (
          <div className="flex gap-2 items-center">
            <p className="px-3 py-2.5 bg-white rounded-md w-full text-sm">
              {selectedTeam.name}
            </p>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onChange(undefined)}
            >
              <Trash />
            </Button>
          </div>
        )}
      </div>
    );
  },
);

SearchTeamCombobox.displayName = "SearchStateCombobox";

export default SearchTeamCombobox;
